/**
 * BugContext — Global tracker that silently captures errors, failed API calls,
 * last clicked element, and screen info for the Bug Reporter.
 */

const MAX_BUFFER = 10;

const bugContext = {
  consoleErrors: [],
  apiFailures: [],
  lastClickedElement: null,
  _initialized: false,

  init() {
    if (this._initialized) return;
    this._initialized = true;

    // 1. Console errors
    const origOnError = window.onerror;
    window.onerror = (message, source, lineno, colno, error) => {
      this.consoleErrors.push({
        type: "error",
        message: String(message),
        source: source || "",
        line: lineno,
        col: colno,
        stack: error?.stack?.split("\n").slice(0, 4).join("\n") || "",
        timestamp: new Date().toISOString(),
      });
      if (this.consoleErrors.length > MAX_BUFFER) this.consoleErrors.shift();
      if (origOnError) origOnError(message, source, lineno, colno, error);
    };

    const origOnUnhandled = window.onunhandledrejection;
    window.onunhandledrejection = (event) => {
      const reason = event.reason;
      this.consoleErrors.push({
        type: "unhandled_promise",
        message: reason?.message || String(reason),
        stack: reason?.stack?.split("\n").slice(0, 4).join("\n") || "",
        timestamp: new Date().toISOString(),
      });
      if (this.consoleErrors.length > MAX_BUFFER) this.consoleErrors.shift();
      if (origOnUnhandled) origOnUnhandled(event);
    };

    // 2. Last clicked element tracker
    document.addEventListener("click", (e) => {
      const el = e.target;
      if (!el) return;
      const tag = el.tagName?.toLowerCase() || "";
      const text = (el.textContent || "").trim().substring(0, 60);
      const testId = el.getAttribute("data-testid") || "";
      const cls = el.className?.toString?.().substring(0, 80) || "";
      const id = el.id || "";
      const role = el.getAttribute("role") || "";
      const href = el.getAttribute("href") || "";

      this.lastClickedElement = {
        tag,
        text: text || "(no text)",
        testId,
        id,
        className: cls,
        role,
        href,
        selector: _buildSelector(el),
        timestamp: new Date().toISOString(),
      };
    }, { capture: true, passive: true });
  },

  // Register a failed API call (called from axios interceptor)
  addApiFailure(method, url, status, statusText, responseData) {
    const detail = typeof responseData === "object"
      ? (responseData?.detail || JSON.stringify(responseData).substring(0, 200))
      : String(responseData || "").substring(0, 200);

    this.apiFailures.push({
      method: (method || "").toUpperCase(),
      url: url || "",
      status,
      statusText: statusText || "",
      detail,
      timestamp: new Date().toISOString(),
    });
    if (this.apiFailures.length > MAX_BUFFER) this.apiFailures.shift();
  },

  // Get active tab from the page
  getActiveTab() {
    // Check for data-state="active" on tab triggers (Radix/Shadcn)
    const activeTab = document.querySelector('[role="tab"][data-state="active"]');
    if (activeTab) {
      return activeTab.textContent?.trim() || "";
    }
    // Fallback: check for aria-selected
    const selected = document.querySelector('[role="tab"][aria-selected="true"]');
    if (selected) {
      return selected.textContent?.trim() || "";
    }
    return "";
  },

  // Get screen/viewport info
  getScreenInfo() {
    return {
      screenResolution: `${window.screen.width}x${window.screen.height}`,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      devicePixelRatio: window.devicePixelRatio || 1,
      isMobile: window.innerWidth < 768,
    };
  },

  // Get snapshot of all captured context for the bug report
  getSnapshot() {
    return {
      consoleErrors: [...this.consoleErrors],
      apiFailures: [...this.apiFailures],
      lastClickedElement: this.lastClickedElement ? { ...this.lastClickedElement } : null,
      activeTab: this.getActiveTab(),
      screen: this.getScreenInfo(),
    };
  },

  // Auto-generate title from most recent error/failure
  suggestTitle() {
    const lastApi = this.apiFailures[this.apiFailures.length - 1];
    const lastErr = this.consoleErrors[this.consoleErrors.length - 1];

    if (lastApi && isRecent(lastApi.timestamp)) {
      return `API ${lastApi.status} error on ${lastApi.method} ${lastApi.url.split("/api")[1] || lastApi.url}`;
    }
    if (lastErr && isRecent(lastErr.timestamp)) {
      return lastErr.message.substring(0, 100);
    }
    return "";
  },

  // Auto-generate description from captured context
  suggestDescription() {
    const parts = [];
    const lastApi = this.apiFailures[this.apiFailures.length - 1];
    const lastErr = this.consoleErrors[this.consoleErrors.length - 1];

    if (lastApi && isRecent(lastApi.timestamp)) {
      parts.push(`**API Error**: ${lastApi.method} ${lastApi.url} returned ${lastApi.status} ${lastApi.statusText}`);
      if (lastApi.detail) parts.push(`Response: ${lastApi.detail}`);
    }
    if (lastErr && isRecent(lastErr.timestamp)) {
      parts.push(`**Console Error**: ${lastErr.message}`);
      if (lastErr.stack) parts.push(`Stack: ${lastErr.stack.split("\n")[0]}`);
    }
    return parts.join("\n");
  },

  // Clear buffers (after successful report)
  clear() {
    this.consoleErrors = [];
    this.apiFailures = [];
  },
};

function isRecent(timestamp) {
  return Date.now() - new Date(timestamp).getTime() < 5 * 60 * 1000; // within 5 minutes
}

function _buildSelector(el) {
  if (!el || !el.tagName) return "";
  const tag = el.tagName.toLowerCase();
  if (el.id) return `${tag}#${el.id}`;
  const testId = el.getAttribute("data-testid");
  if (testId) return `[data-testid="${testId}"]`;
  const cls = el.className?.toString?.().split(" ").filter(c => c && !c.includes(":"))[0];
  if (cls) return `${tag}.${cls}`;
  return tag;
}

export default bugContext;
